import { Panel, trendGlyph, trendClass } from "../Panel"
import type { PortCondition, PortPrediction } from "../dashboard-types"
import { useRegion } from "../store"
import { Sparkline } from "./Sparkline"

function congestionColor(score: number): string {
  if (score >= 70) return "bg-red-500"
  if (score >= 50) return "bg-orange-400"
  if (score >= 30) return "bg-amber-400"
  return "bg-emerald-400"
}

function congestionBarColor(score: number): string {
  if (score >= 70) return "bg-red-500/70"
  if (score >= 50) return "bg-orange-400/70"
  if (score >= 30) return "bg-amber-400/70"
  return "bg-emerald-400/70"
}

function seaStateLabel(state: string): string {
  switch (state) {
    case "calm": return "Calm"
    case "slight": return "Slight"
    case "moderate": return "Mod"
    case "rough": return "Rough"
    default: return state
  }
}

interface Props {
  ports: PortCondition[]
  portPredictions?: PortPrediction[]
  demo?: boolean
}

export function PortConditions({ ports, portPredictions, demo }: Props) {
  const openRegion = useRegion((s) => s.openRegion)

  // Build prediction lookup
  const predMap = new Map<string, PortPrediction>()
  if (portPredictions) {
    for (const pp of portPredictions) {
      predMap.set(pp.port_id, pp)
    }
  }

  return (
    <Panel
      title="PORT CONDITIONS"
      live
      demo={demo}
      count={ports.length}
      helpText="Real-time port congestion, berth wait times, vessel queues, and weather for major global ports. Includes 7-day congestion forecasts."
    >
      <div className="divide-y divide-[color:var(--border)]">
        {ports.map((p) => {
          const pred = predMap.get(p.id)
          return (
            <button
              key={p.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-[color:var(--surface-alt)]/40 transition-colors cursor-pointer"
              onClick={() => openRegion(p.country)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") openRegion(p.country)
              }}
            >
              {/* Row 1: Port name + LOCODE + congestion dot + trend + prediction badge */}
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${congestionColor(p.congestion)}`} />
                <span className="text-xs font-semibold text-[color:var(--text)] truncate">{p.port}</span>
                <span className="text-[9px] text-[color:var(--text-dim)] uppercase tracking-wider">{p.locode}</span>
                <span className="flex-1" />
                {pred && (
                  <span className={`text-[8px] tabular-nums font-mono px-1 py-0 rounded ${
                    pred.direction === "worsening"
                      ? "text-red-400 bg-red-500/10"
                      : pred.direction === "improving"
                        ? "text-emerald-400 bg-emerald-500/10"
                        : "text-[color:var(--text-muted)] bg-[color:var(--surface-alt)]/50"
                  }`}>
                    {pred.direction === "worsening" ? "\u2191" : pred.direction === "improving" ? "\u2193" : "\u2192"} 7d
                  </span>
                )}
                <span className={`text-[10px] tabular-nums ${trendClass(p.trend)}`}>
                  {trendGlyph(p.trend)}
                </span>
              </div>

              {/* Row 2: Congestion bar + optional forecast sparkline */}
              <div className="mt-1.5 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-[color:var(--surface-alt)] overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${congestionBarColor(p.congestion)}`}
                    style={{ width: `${Math.min(100, p.congestion)}%` }}
                  />
                </div>
                <span className="text-[9px] tabular-nums text-[color:var(--text-muted)] w-8 text-right">
                  {p.congestion}%
                </span>
                {pred && pred.congestion_7d.length > 0 && (
                  <div className="w-14 shrink-0" title="7-day congestion forecast">
                    <Sparkline
                      values={pred.congestion_7d}
                      height={12}
                      stroke={
                        pred.direction === "worsening" ? "#ef4444"
                          : pred.direction === "improving" ? "#10b981"
                            : "#8a8a8a"
                      }
                    />
                  </div>
                )}
              </div>

              {/* Row 3: Stats grid */}
              <div className="mt-1.5 flex items-center gap-3 text-[9px] text-[color:var(--text-muted)]">
                <span className="flex items-center gap-1">
                  <span className="uppercase tracking-wider">Wait</span>
                  <span className="tabular-nums text-[color:var(--text)]">{p.berth_wait_hrs}h</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="uppercase tracking-wider">Queue</span>
                  <span className="tabular-nums text-[color:var(--text)]">{p.vessel_queue}</span>
                </span>
                <span className="text-[color:var(--border)]">|</span>
                <span className="flex items-center gap-1">
                  <span className="tabular-nums text-[color:var(--text)]">{p.weather.temp_c}&deg;C</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="tabular-nums text-[color:var(--text)]">{p.weather.wind_kt}kt</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="text-[color:var(--text)]">{seaStateLabel(p.weather.sea_state)}</span>
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </Panel>
  )
}
